import api from '@common/api';
import config from '@common/config';
import logging from '@common/logging';
import { AutoInvestmentsModel } from '@common/models/AutoInvestmentsModel';
import { BankAccountsModel } from '@common/models/BankAccountsModel';
import { InvestorAccountModel } from '@common/models/InvestorAccountModel';
import { MyProfileModel } from '@common/models/MyProfileModel';
import { SubscriptionModel } from '@common/models/SubscriptionModel';
import storage from '@common/storage';
import support from '@common/support';
import { FrequencyMap, WeekdayMap } from '@services/appsync/mappings';
import { CreateAutoInvestScheduleV2MutationVariables, PricingPlan, SignatureMethod } from '@services/appsync/types';
import create from 'zustand';
import { PAGES, SUGGESTED_BI_MONTHLY, SUGGESTED_DAILY, SUGGESTED_MONTHLY, SUGGESTED_WEEKLY } from '../constants';
import { InvestPreflightHint } from '@services/appsync/types';
import { fetchAuthSession } from 'aws-amplify/auth';

interface CheckoutCashflowViewModelProps {
  existingAutoInvest: string | null;
  amount: number;
  confirmAgreement: boolean;
  frequency: FrequencyMap;
  page: PAGES;
  pricingPlan?: PricingPlan;
  newSignatureBlob?: Blob;
  newSignatureDataUrl?: string;
  dayOfWeek?: number | null;
  daysOfMonth?: number[] | null;
}

interface CheckoutCashflowViewModelActions {
  getDayNameByNumber: (dayNumber: WeekdayMap) => string;
  getFrequencyDescription: () => string;
  getSuggestedAmounts: () => {
    minAmount: number;
    maxAmount: number;
    popularAmount: number;
    suggestedAmount: number;
    suggestedAmounts: number[];
  };
  handleAmountFormSubmit: () => Promise<AmountFormSubmitResult>;
  initController: (settings?: Partial<CheckoutCashflowViewModelProps>) => Promise<void>;
  invest: () => Promise<void>;
  needsToPickPricingPlan: () => boolean;
  setAmount: (amount: number) => void;
  setDayOfWeek: (day: number | null) => void;
  setDaysOfMonth: (days: number[] | null) => void;
  setConfirmAgreement: (confirmAgreement: boolean) => void;
  setFrequency: (freqency: FrequencyMap) => void;
  setNewSignatureBlob: (blog?: Blob) => void;
  setNewSignatureDataUrl: (dataUrl?: string) => void;
  setPage: (page: PAGES) => void;
  setPricingPlan: (pricingPlan: PricingPlan) => void;
}

interface AmountFormSubmitResult {
  success: boolean;
  error?: string;
  preflightHints?: InvestPreflightHint[];
}

const defaults: CheckoutCashflowViewModelProps = {
  amount: 0,
  confirmAgreement: false,
  existingAutoInvest: null,
  frequency: FrequencyMap.ONE,
  newSignatureBlob: undefined,
  newSignatureDataUrl: undefined,
  page: PAGES.amount,
  pricingPlan: undefined,
};

export const useCheckoutCashflowViewModel = create<CheckoutCashflowViewModelProps & CheckoutCashflowViewModelActions>(
  (set, get) => ({
    ...defaults,
    getDayNameByNumber: (dayNumber) => {
      switch (dayNumber) {
        case WeekdayMap.MONDAY:
          return 'Monday';
        case WeekdayMap.TUESDAY:
          return 'Tuesday';
        case WeekdayMap.WEDNESDAY:
          return 'Wednesday';
        case WeekdayMap.THURSDAY:
          return 'Thursday';
        case WeekdayMap.FRIDAY:
          return 'Friday';
        case WeekdayMap.SATURDAY:
          return 'Saturday';
        case WeekdayMap.SUNDAY:
          return 'Sunday';
        default:
          return 'Monday';
      }
    },
    getFrequencyDescription: () => {
      // TODO: Once we implement auto-invest, we need to uncomment this
      //const { getDayNameByNumber } = get();
      const daysOfMonth = get().daysOfMonth ?? null;
      const dayOfWeek = get().dayOfWeek ?? null;

      return AutoInvestmentsModel.getState().getFrequencyDescription({
        dayOfWeek,
        daysOfMonth,
        frequency: get().frequency,
      });
    },
    getSuggestedAmounts: () => {
      const { subscriptionOffer } = InvestorAccountModel.getState();

      // TODO: Get all these lists from the backend
      let amountList: number[] = [];
      let maxAmount: number;
      let minAmount: number;
      switch (get().frequency) {
        case FrequencyMap.ONE:
          amountList = subscriptionOffer?.suggested_invest_amounts ?? [];
          maxAmount = subscriptionOffer?.max_invest_amount ?? 100000000;
          minAmount = subscriptionOffer?.min_invest_amount ?? 0;
          break;
        case FrequencyMap.DAILY:
          amountList = SUGGESTED_DAILY;
          maxAmount = Math.round((subscriptionOffer?.max_invest_amount ?? 100000000 * 12) / 365);
          minAmount = Math.round((subscriptionOffer?.min_invest_amount ?? 0 * 12) / 365);
          break;
        case FrequencyMap.WEEKLY:
          amountList = SUGGESTED_WEEKLY;
          maxAmount = Math.round((subscriptionOffer?.max_invest_amount ?? 100000000 * 12) / 52);
          minAmount = Math.round((subscriptionOffer?.min_invest_amount ?? 0 * 12) / 52);
          break;
        case FrequencyMap.BI_MONTHLY:
          amountList = SUGGESTED_BI_MONTHLY;
          maxAmount = Math.round((subscriptionOffer?.max_invest_amount ?? 100000000) / 2);
          minAmount = Math.round((subscriptionOffer?.min_invest_amount ?? 0) / 2);
          break;
        case FrequencyMap.MONTHLY:
          amountList = SUGGESTED_MONTHLY;
          maxAmount = subscriptionOffer?.max_invest_amount ?? 100000000;
          minAmount = subscriptionOffer?.min_invest_amount ?? 0;
          break;
        default:
          maxAmount = subscriptionOffer?.max_invest_amount ?? 100000000;
          minAmount = subscriptionOffer?.min_invest_amount ?? 0;
          amountList = subscriptionOffer?.suggested_invest_amounts ?? [];
      }

      const suggestedAmounts = amountList.filter((amount) => amount <= maxAmount && amount >= minAmount);
      const popularAmount = suggestedAmounts[Math.ceil(suggestedAmounts.length / 2)];
      const suggestedAmount = amountList.length === 0 ? minAmount : popularAmount;

      return {
        maxAmount,
        minAmount,
        popularAmount,
        suggestedAmount,
        suggestedAmounts,
      };
    },
    handleAmountFormSubmit: async () => {
      const { amount, needsToPickPricingPlan, pricingPlan, setPage } = get();
      const { investorAccount, subscriptionOffer } = InvestorAccountModel.getState();

      try {
        // Set the pricing plan state
        const _pricingPlan =
          needsToPickPricingPlan() && amount < config.MIN_ACCOUNT_BALANCE_FEE_THRESHOLD
            ? pricingPlan ?? PricingPlan.FIFTY_DOLLARS_PER_YEAR_UNDER_5K
            : undefined;
        set({ amount, pricingPlan: _pricingPlan });
        // Do the preflight check
        const result = await api.getInvestPreflightInfo({
          amount,
          investor_account_id: investorAccount.id,
          subscription_offer_id: subscriptionOffer.id,
        });
        const preflightHints = result?.hints ?? [];
        const retVal = { preflightHints, success: !preflightHints || preflightHints.length === 0 };

        if (retVal.success) {
          setPage(_pricingPlan ? PAGES.pricingPlan : PAGES.confirmation);
        }
        return retVal;
      } catch (err: any) {
        logging.logError(err);
        return { error: 'System Error', success: false };
      }
    },
    initController: async (settings?: Partial<CheckoutCashflowViewModelProps>) => {
      const { checkCashflowEligibility, fetchInvestorAccount } = InvestorAccountModel.getState();
      const investorAccount = (await fetchInvestorAccount()).investorAccount ?? undefined;
      const amplifySession = await fetchAuthSession();
      if (!investorAccount) throw new Error('Investor account not found');
      if (!amplifySession.userSub) throw new Error('No user sub found');
      const { fetchBankAccounts } = BankAccountsModel.getState();
      const _bankAccounts = (await fetchBankAccounts()).bankAccounts ?? [];
      await checkCashflowEligibility({ bankAccounts: _bankAccounts });
      const { fetchMyProfile, getStoredSignatureUrl } = MyProfileModel.getState();
      await fetchMyProfile(amplifySession.userSub);
      await getStoredSignatureUrl();
      const { suggestedAmount } = get().getSuggestedAmounts();

      if (settings?.existingAutoInvest) {
        const autoInvest = (await AutoInvestmentsModel.getState().getAutoInvest(settings.existingAutoInvest)).data;
        settings.amount = autoInvest?.amount;
        settings.dayOfWeek =
          autoInvest?.days_of_week && autoInvest.days_of_week.length > 0 ? autoInvest.days_of_week[0] : undefined;
        settings.daysOfMonth = autoInvest?.days_of_month;
        settings.frequency = (autoInvest?.frequency as FrequencyMap) ?? FrequencyMap.ONE;

        if (settings.frequency === FrequencyMap.MONTHLY) {
          if (autoInvest?.days_of_month && autoInvest?.days_of_month?.length > 1) {
            settings.frequency = FrequencyMap.BI_MONTHLY;
          }
        }
      }

      // setup state
      set({
        ...defaults,
        ...{ amount: suggestedAmount },
        ...settings,
      });
    },
    invest: async () => {
      const { amount, pricingPlan, needsToPickPricingPlan, newSignatureBlob, newSignatureDataUrl } = get();
      const { investorAccount, subscriptionOffer, updateBrokerDealerAssociation } = InvestorAccountModel.getState();
      if (!investorAccount) throw new Error('Investor account not found');
      if (!subscriptionOffer) throw new Error('Subscription offer not found');

      // store signature
      if (newSignatureBlob) {
        const result = await storage.saveSignature(newSignatureBlob);
        if (!result) throw new Error('There was an error saving your signature. Please contact support.');
        // also update the signature url in the profile so we don't have to refetch it
        MyProfileModel.getState().setStoredSignatureUrl(newSignatureDataUrl);
      }

      // if they haven't answered the broker dealer association question, set it to false
      if ((investorAccount?.non_accredited_survey_v2?.broker_dealer_assoc?.value ?? null) === null) {
        await updateBrokerDealerAssociation(false);
      }

      if (needsToPickPricingPlan()) {
        // set broker-dealer affiliation to no for first investment
        support.update({ customAttributes: { affiliation_bd: false } });

        // set pricing plan for users below min balance
        if (pricingPlan) {
          await api.setPricingPlan({
            investor_account_id: investorAccount.id,
            pricing_plan: pricingPlan,
          });
        }
      }

      const { dayOfWeek, daysOfMonth, frequency } = get();
      let result;
      try {
        if (frequency !== FrequencyMap.ONE) {
          const request: CreateAutoInvestScheduleV2MutationVariables = {
            amount: get().amount ?? 0,
            frequency: 'MONTHLY',
            goal_id: undefined,
            interval: 1,
            investor_account_id: investorAccount.id,
            subscription_offer_id: subscriptionOffer.id,
          };

          switch (frequency) {
            case FrequencyMap.DAILY:
              request.frequency = 'DAILY';
              break;
            case FrequencyMap.WEEKLY:
              request.days_of_week = dayOfWeek !== null ? [dayOfWeek ?? WeekdayMap.MONDAY] : null;
              request.frequency = 'WEEKLY';
              break;
            case FrequencyMap.BI_MONTHLY:
              request.days_of_month = [1, 15];
              request.frequency = 'MONTHLY';
              break;
            case FrequencyMap.MONTHLY:
              request.days_of_month = daysOfMonth ?? null;
              request.frequency = 'MONTHLY';
              break;
          }

          const existingAutoInvest = get().existingAutoInvest;
          if (existingAutoInvest) {
            result = await api.modifyAutoInvestSchedule({
              amount: request.amount,
              autoinvest_id: existingAutoInvest,
              days_of_month: request.days_of_month,
              days_of_week: request.days_of_week,
              frequency: request.frequency,
              interval: request.interval,
              investor_account_id: request.investor_account_id,
            });
          } else {
            result = await api.createAutoInvestScheduleV2(request);
          }
          await AutoInvestmentsModel.getState().fetchAutoInvestments();
        } else {
          result = await api.invest({
            amount,
            investor_account_id: investorAccount.id,
            signature_method: SignatureMethod.INTERNAL,
            subscription_offer_id: subscriptionOffer.id,
          });
        }
        if (!result) throw new Error();
      } catch (err) {
        logging.logError(err as Error);
        throw new Error('There was an error submitting your investment. Please contact support.');
      }

      // refresh subscriptions
      await SubscriptionModel.getState().fetchSubscriptions(investorAccount.id);

      // track investment
      support.trackEvent('invest', { amount: String(amount) });

      // change page to congrats
      set({ page: PAGES.congrats });
    },
    needsToPickPricingPlan: () => {
      // XXX: To add back once subscription_offer_id is added to the trades
      return false;
      // const { investorAccount } = InvestorAccountModel.getState();
      // if (investorAccount?.claims_is_accredited) return false;
      // const { trades } = TradesModel.getState();
      // const _trades = trades ?? [];
      // const hasBrokerDealerAssociation = investorAccount?.non_accredited_survey_v2?.broker_dealer_assoc?.value ?? false;
      // const hasAregATrade = _trades.some((trade) => trade.subscription_offer?.id === config.SUBSCRIPTION_OFFERS.regA);
      // return hasBrokerDealerAssociation === false && !hasAregATrade;
    },
    setAmount: (amount: number) => {
      set({ amount });
    },
    setConfirmAgreement: (confirmAgreement: boolean) => {
      set({ confirmAgreement });
    },
    setDayOfWeek: (day) => {
      set({ dayOfWeek: day });
    },
    setDaysOfMonth: (days) => {
      set({ daysOfMonth: days });
    },
    setFrequency: (frequency: FrequencyMap) => {
      let daysOfMonth = get().daysOfMonth ?? null;
      let dayOfWeek = get().dayOfWeek ?? null;

      if (frequency === FrequencyMap.BI_MONTHLY) {
        daysOfMonth = [1, 15];
      }

      if (frequency === FrequencyMap.MONTHLY && daysOfMonth === null) {
        daysOfMonth = [1];
      }

      if (frequency === FrequencyMap.WEEKLY && dayOfWeek === null) {
        dayOfWeek = 0;
      }
      set({ dayOfWeek, daysOfMonth, frequency });
    },
    setNewSignatureBlob: (blob?: Blob) => {
      set({ newSignatureBlob: blob });
    },
    setNewSignatureDataUrl: (dataUrl?: string) => {
      set({ newSignatureDataUrl: dataUrl });
    },
    setPage: (page: PAGES) => {
      set({ page });
    },
    setPricingPlan: (pricingPlan: PricingPlan) => {
      set({ pricingPlan });
    },
  }),
);
